import { Injectable, NgZone } from '@angular/core';
import { CoreDataService } from '@app/core/async-services/http/core.data';
import { ApiVersionedRoute } from '@app/core/async-services/http/versioned/api-versions';
import { Platform } from '@ionic/angular';
import { BehaviorSubject, EMPTY, Observable, timer } from 'rxjs';
import { catchError, filter, first, map, switchMap } from 'rxjs/operators';
import { FeatureFlagName, FeatureFlags } from './feature-flag.interface';

const minutes = (totalMinutes: number) => totalMinutes * 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  canViewTaskNotifications$: Observable<boolean>;
  private _featureFlags$ = new BehaviorSubject<FeatureFlags | null>(null);

  get features(): FeatureFlags | null {
    return this._featureFlags$.value;
  }

  constructor(
    private _coreDataService: CoreDataService,
    private _ngZone: NgZone,
    private _platform: Platform,
  ) {
    this._ngZone.runOutsideAngular(async () => {
      await this._platform.ready();
      timer(0, minutes(5))
        .pipe(
          switchMap(() => this._getFeatureFlags$()),
          catchError(() => EMPTY),
        )
        .subscribe((flags) => {
          this._ngZone.run(() => {
            this._featureFlags$.next(flags);
          });
        });
    });

    this.canViewTaskNotifications$ = this._featureFlags$.pipe(
      filter((flags) => flags !== null),
      map((flags) => flags?.taskNotifications ?? false),
      first(),
    );
  }

  get isTasksFlagOn$(): Observable<boolean> {
    return this._isFeatureOn$(FeatureFlagName.tasks);
  }

  get isRealtimeRoomsFlagOn$(): Observable<boolean> {
    return this._isFeatureOn$(FeatureFlagName.realtimeRooms);
  }

  get isRealtimeRoomsPortfolioFlagOn$(): Observable<boolean> {
    return this._isFeatureOn$(FeatureFlagName.realtimeRoomsPortfolio);
  }

  get isShiftSwapRequestsFlagOn$(): Observable<boolean> {
    return this._isFeatureOn$(FeatureFlagName.shiftSwapRequests);
  }

  get isPendoFeatureFlagOn$(): Observable<boolean> {
    return this._isFeatureOn$(FeatureFlagName.pendo);
  }

  get isInMaintenance(): boolean {
    return this.isFeatureOn(FeatureFlagName.maintenanceSplash) ?? false;
  }

  get isInMaintenance$(): Observable<boolean> {
    return this._isFeatureOn$(FeatureFlagName.maintenanceSplash);
  }

  get isRealtimeRoomsNewTaskStatusesOn$(): Observable<boolean> {
    return this._isFeatureOn$(FeatureFlagName.realtimeRoomsNewTaskStatuses);
  }

  get isInspectedRoomStatusOn$(): Observable<boolean> {
    return this._isFeatureOn$(FeatureFlagName.inspectedRoomStatus);
  }

  isFeatureOn(flag: FeatureFlagName): boolean {
    return !!this.features?.[flag];
  }

  isFeatureOff(flag: FeatureFlagName): boolean {
    return !this.isFeatureOn(flag);
  }

  private _getFeatureFlags$(): Observable<FeatureFlags> {
    return this._coreDataService.get<FeatureFlags>(
      `${ApiVersionedRoute.featureFlags}/all`,
    );
  }

  private _isFeatureOn$(flag: FeatureFlagName): Observable<boolean> {
    return this._featureFlags$.pipe(
      map((flags) => flags !== null && flags?.[flag] === true),
    );
  }
}
